/**
 * Procurement & Supply — Vehicle Codes, Assignments, Inspections, Maintenance.
 *
 * Two boards on one panel: Field Fleet (VAL/CLN + region) and Admin Fleet
 * (CEO/DIR/MGR/SUP + position). Codes are auto-generated and read-only.
 *
 * Mounted inside ProcurementCommandCenter (DeptCommandCenters.jsx).
 */
import { useEffect, useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Truck, Plus, UserCheck, Wrench, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const DEPT_CODE_OPTS = [
  { value: 'CEO', label: 'CEO Office' },
  { value: 'DIR', label: 'Directors' },
  { value: 'MGR', label: 'Managers' },
  { value: 'SUP', label: 'Supervisors' },
];

const ROLE_CODE_OPTS = [
  { value: 'VAL', label: 'Valet (VAL)' },
  { value: 'CLN', label: 'Cleaners (CLN)' },
];

function api(path, opts = {}) {
  return fetch(`${API}/api${path}`, { credentials: 'include', ...opts });
}

export default function ProcurementVehiclesPanel() {
  const [board, setBoard] = useState('field');
  const [vehicles, setVehicles] = useState([]);
  const [filters, setFilters] = useState({ state: '', region_code: '', role_code: '', dept_code: '', position_code: '', vehicle_number: '' });
  const [staff, setStaff] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [assignFor, setAssignFor] = useState(null); // vehicle_id
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ board });
      Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
      const r = await api(`/procurement/vehicles?${q.toString()}`);
      const data = r.ok ? await r.json() : [];
      setVehicles(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  };

  const loadStaff = async () => {
    // Reuse eligible-employees list so assignment dropdown has real names.
    const kind = board === 'field' ? 'staff' : 'admin';
    const r = await api(`/vehicles/eligible-employees?fleet_kind=${kind}`);
    const data = r.ok ? await r.json() : [];
    setStaff(Array.isArray(data) ? data : []);
  };

  useEffect(() => { load(); loadStaff(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [board]);

  const doAssign = async (vehicleId, userId) => {
    const r = await api(`/procurement/vehicles/${vehicleId}/assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId }),
    });
    if (r.ok) {
      toast.success('Vehicle assigned');
      setAssignFor(null);
      load();
    } else {
      const j = await r.json().catch(() => ({}));
      toast.error(j.detail || 'Assign failed');
    }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 mt-4" data-testid="procurement-vehicles-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.4)' }}>
            <Truck className="h-5 w-5" style={{ color: '#a855f7' }} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Vehicle Codes &amp; Assignments
            </h3>
            <p className="text-xs text-zinc-400">Auto-generated · read-only once issued</p>
          </div>
        </div>
        <Button
          onClick={() => setAddOpen(true)}
          className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold"
          data-testid="procurement-add-vehicle-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> Add Vehicle
        </Button>
      </div>

      <Tabs value={board} onValueChange={setBoard} className="w-full">
        <TabsList className="bg-zinc-800 border border-zinc-700 mb-3">
          <TabsTrigger value="field" data-testid="procurement-tab-field">Field Fleet</TabsTrigger>
          <TabsTrigger value="admin" data-testid="procurement-tab-admin">Admin / Management Fleet</TabsTrigger>
        </TabsList>

        <TabsContent value="field" className="mt-0">
          <FiltersRow
            board="field"
            filters={filters}
            setFilters={setFilters}
            onApply={load}
          />
          <VehicleTable
            vehicles={vehicles}
            board="field"
            onAssignClick={(v) => setAssignFor(v)}
            loading={loading}
          />
        </TabsContent>

        <TabsContent value="admin" className="mt-0">
          <FiltersRow
            board="admin"
            filters={filters}
            setFilters={setFilters}
            onApply={load}
          />
          <VehicleTable
            vehicles={vehicles}
            board="admin"
            onAssignClick={(v) => setAssignFor(v)}
            loading={loading}
          />
        </TabsContent>
      </Tabs>

      <AddVehicleDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        board={board}
        onCreated={load}
      />
      <AssignDialog
        vehicle={assignFor}
        staff={staff}
        onClose={() => setAssignFor(null)}
        onAssign={doAssign}
      />
    </Card>
  );
}

function FiltersRow({ board, filters, setFilters, onApply }) {
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  return (
    <div className="flex flex-wrap gap-2 mb-4" data-testid={`procurement-filters-${board}`}>
      <Input
        placeholder="State (e.g. TX)"
        value={filters.state}
        onChange={(e) => set('state', e.target.value.toUpperCase().slice(0, 2))}
        className="w-24 bg-zinc-800 border-zinc-700 text-white"
        data-testid={`filter-state-${board}`}
      />
      {board === 'field' ? (
        <>
          <Input placeholder="Region (R01)" value={filters.region_code} onChange={(e) => set('region_code', e.target.value.toUpperCase())} className="w-28 bg-zinc-800 border-zinc-700 text-white" data-testid="filter-region" />
          <Select value={filters.role_code || 'all'} onValueChange={(v) => set('role_code', v === 'all' ? '' : v)}>
            <SelectTrigger className="w-36 bg-zinc-800 border-zinc-700 text-white" data-testid="filter-role"><SelectValue placeholder="Role" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {ROLE_CODE_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </>
      ) : (
        <>
          <Select value={filters.dept_code || 'all'} onValueChange={(v) => set('dept_code', v === 'all' ? '' : v)}>
            <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700 text-white" data-testid="filter-dept"><SelectValue placeholder="Dept" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All dept tiers</SelectItem>
              {DEPT_CODE_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Position (OPS)" value={filters.position_code} onChange={(e) => set('position_code', e.target.value.toUpperCase().slice(0, 3))} className="w-32 bg-zinc-800 border-zinc-700 text-white" data-testid="filter-pos" />
        </>
      )}
      <Input placeholder="Vehicle # (001)" value={filters.vehicle_number} onChange={(e) => set('vehicle_number', e.target.value)} className="w-32 bg-zinc-800 border-zinc-700 text-white" data-testid="filter-num" />
      <Button variant="secondary" onClick={onApply} data-testid="filter-apply">Apply</Button>
    </div>
  );
}

function VehicleTable({ vehicles, board, onAssignClick, loading }) {
  if (loading) return <div className="text-sm text-zinc-500 py-6 text-center">Loading vehicles…</div>;
  if (!vehicles.length) return <div className="text-sm text-zinc-500 py-6 text-center">No vehicles yet. Add one above.</div>;
  return (
    <div className="overflow-x-auto" data-testid={`procurement-vehicle-table-${board}`}>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] uppercase text-zinc-500 border-b border-zinc-800">
            <th className="text-left py-2 pr-3">Code</th>
            <th className="text-left py-2 pr-3">Vehicle</th>
            <th className="text-left py-2 pr-3">VIN</th>
            <th className="text-left py-2 pr-3">Mileage</th>
            <th className="text-left py-2 pr-3">Assigned To</th>
            <th className="text-left py-2 pr-3">Status</th>
            <th className="text-right py-2 pl-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {vehicles.map((v) => (
            <tr key={v.vehicle_id} className="border-b border-zinc-800/60 text-zinc-200 hover:bg-zinc-800/30">
              <td className="py-3 pr-3 font-mono text-[#FFD60A]" data-testid={`row-code-${v.vehicle_id}`}>{v.vehicle_code}</td>
              <td className="py-3 pr-3">
                <div>{v.color} {v.model}</div>
                <div className="text-[11px] text-zinc-500">{v.vehicle_type}</div>
              </td>
              <td className="py-3 pr-3 font-mono text-[11px] text-zinc-400">{v.vin}</td>
              <td className="py-3 pr-3">{(v.current_mileage || 0).toLocaleString()}</td>
              <td className="py-3 pr-3">
                {v.assignment ? (
                  <div>
                    <div className="font-medium">{v.assignment.name}</div>
                    <div className="text-[11px] text-zinc-500">{v.assignment.position}</div>
                  </div>
                ) : <span className="text-zinc-500 text-xs">Unassigned</span>}
              </td>
              <td className="py-3 pr-3">
                <div className="flex flex-col gap-1 items-start">
                  {v.maintenance_flag?.active ? (
                    <Badge className="bg-red-600 text-white"><AlertTriangle className="h-3 w-3 mr-1" /> Maintenance Due</Badge>
                  ) : (
                    <Badge className="bg-emerald-700 text-white">Operational</Badge>
                  )}
                  {v.mpg_anomaly && (
                    <Badge className="bg-orange-600 text-white" data-testid={`mpg-anomaly-badge-${v.vehicle_id}`}>
                      <AlertTriangle className="h-3 w-3 mr-1" /> MPG anomaly
                    </Badge>
                  )}
                </div>
              </td>
              <td className="py-3 pl-3 text-right">
                <Button size="sm" variant="secondary" onClick={() => onAssignClick(v)} data-testid={`assign-btn-${v.vehicle_id}`}>
                  <UserCheck className="h-4 w-4 mr-1" /> {v.assignment ? 'Reassign' : 'Assign'}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AddVehicleDialog({ open, onClose, board, onCreated }) {
  const [form, setForm] = useState({
    state: 'TX', region_code: '', role_code: 'VAL',
    dept_code: 'DIR', position_code: '',
    vehicle_type: '', model: '', color: '', vin: '', current_mileage: 0,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    const payload = {
      board,
      state: form.state,
      vehicle_type: form.vehicle_type,
      model: form.model,
      color: form.color,
      vin: form.vin,
      current_mileage: Number(form.current_mileage || 0),
      ...(board === 'field'
        ? { region_code: form.region_code, role_code: form.role_code }
        : { dept_code: form.dept_code, position_code: form.position_code }),
    };
    const r = await fetch(`${API}/api/procurement/vehicles`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    setSubmitting(false);
    if (r.ok) {
      const v = await r.json();
      toast.success(`Vehicle added · ${v.vehicle_code}`);
      onCreated();
      onClose();
    } else {
      const j = await r.json().catch(() => ({}));
      toast.error(j.detail || 'Failed to add vehicle');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl" data-testid="add-vehicle-dialog">
        <DialogHeader>
          <DialogTitle>Add {board === 'field' ? 'Field' : 'Admin'} Vehicle</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="State (2-letter)">
            <Input value={form.state} onChange={(e) => set('state', e.target.value.toUpperCase().slice(0, 2))} className="bg-zinc-800 border-zinc-700" data-testid="add-state" />
          </Field>
          {board === 'field' ? (
            <>
              <Field label="Region Code">
                <Input placeholder="R01" value={form.region_code} onChange={(e) => set('region_code', e.target.value.toUpperCase())} className="bg-zinc-800 border-zinc-700" data-testid="add-region" />
              </Field>
              <Field label="Role Code">
                <Select value={form.role_code} onValueChange={(v) => set('role_code', v)}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="add-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLE_CODE_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </>
          ) : (
            <>
              <Field label="Department Tier">
                <Select value={form.dept_code} onValueChange={(v) => set('dept_code', v)}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="add-dept"><SelectValue /></SelectTrigger>
                  <SelectContent>{DEPT_CODE_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Position Code (e.g. OPS/FIN/SAL/HR)">
                <Input placeholder="OPS" value={form.position_code} onChange={(e) => set('position_code', e.target.value.toUpperCase().slice(0, 3))} className="bg-zinc-800 border-zinc-700" data-testid="add-position" />
              </Field>
            </>
          )}
          <Field label="Vehicle Type"><Input value={form.vehicle_type} onChange={(e) => set('vehicle_type', e.target.value)} placeholder="Van / Truck / SUV" className="bg-zinc-800 border-zinc-700" data-testid="add-type" /></Field>
          <Field label="Model"><Input value={form.model} onChange={(e) => set('model', e.target.value)} placeholder="Ford Transit 250" className="bg-zinc-800 border-zinc-700" data-testid="add-model" /></Field>
          <Field label="Color"><Input value={form.color} onChange={(e) => set('color', e.target.value)} placeholder="White" className="bg-zinc-800 border-zinc-700" data-testid="add-color" /></Field>
          <Field label="VIN"><Input value={form.vin} onChange={(e) => set('vin', e.target.value.toUpperCase())} placeholder="17-char VIN" className="bg-zinc-800 border-zinc-700 font-mono" data-testid="add-vin" /></Field>
          <Field label="Current Mileage"><Input type="number" value={form.current_mileage} onChange={(e) => set('current_mileage', e.target.value)} className="bg-zinc-800 border-zinc-700" data-testid="add-mileage" /></Field>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold" data-testid="add-submit">
            {submitting ? 'Saving…' : 'Add Vehicle & Generate Code'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignDialog({ vehicle, staff, onClose, onAssign }) {
  const [pick, setPick] = useState('');
  useEffect(() => { setPick(vehicle?.assignment?.user_id || ''); }, [vehicle]);
  const eligible = useMemo(() => {
    if (!vehicle) return [];
    if (vehicle.board === 'field') {
      // exclude Site Agents
      return staff.filter((s) => !(s.position || '').toLowerCase().includes('site agent'));
    }
    return staff;
  }, [staff, vehicle]);
  if (!vehicle) return null;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle>Assign {vehicle.vehicle_code}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Label>Staff member</Label>
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="assign-pick"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              {eligible.map((s) => (
                <SelectItem key={s.user_id} value={s.user_id}>
                  {(s.name || s.email)} · {s.position || s.role}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-zinc-500">Code + agent name + position are locked in at assignment time.</p>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => pick && onAssign(vehicle.vehicle_id, pick)} disabled={!pick} className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold" data-testid="assign-submit">
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-zinc-400 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
